import React, { useState, useRef, useEffect, useMemo } from "react";
import { Transaction } from "../types";
import { useAuth } from "../context/AuthContext";
import { useMonth } from "../context/MonthContext";
import { ApiCategoryGroupBudget, transactionApi } from "../services/api";
import { useGroupBudgets } from "../hooks/useGroupBudgets";

interface ExpenseEntryProps {
  onAddTransaction: (transaction: Omit<Transaction, "id">) => void;
}

const LAST_EXPENSE_CATEGORY_KEY_PREFIX = "last_expense_category";

const getLastExpenseCategoryKey = (groupId?: string | null) =>
  groupId
    ? `${LAST_EXPENSE_CATEGORY_KEY_PREFIX}:${groupId}`
    : LAST_EXPENSE_CATEGORY_KEY_PREFIX;

const getStoredLastExpenseCategory = (groupId?: string | null) => {
  try {
    return localStorage.getItem(getLastExpenseCategoryKey(groupId)) || "";
  } catch {
    return "";
  }
};

const storeLastExpenseCategory = (
  category: string,
  groupId?: string | null
) => {
  try {
    localStorage.setItem(getLastExpenseCategoryKey(groupId), category);
  } catch {
    // Ignore storage errors; category selection should still work normally.
  }
};

interface RecentTransactionsProps {
  transactions: Transaction[];
  loading?: boolean;
  categoryGroups: ApiCategoryGroupBudget[];
  onEditTransaction: (transaction: Transaction) => void;
  onDeleteTransaction: (transaction: Transaction) => void;
}

const RecentTransactions: React.FC<RecentTransactionsProps> = ({
  transactions,
  loading = false,
  categoryGroups,
  onEditTransaction,
  onDeleteTransaction,
}) => {
  const { selectedMonth, currentYear } = useMonth();
  const [exportFormat, setExportFormat] = useState<"json" | "excel">("json");
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const categoryFilterRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowExportDropdown(false);
      }
      if (
        categoryFilterRef.current &&
        !categoryFilterRef.current.contains(event.target as Node)
      ) {
        setShowCategoryFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Reset category filter when month changes
  useEffect(() => {
    setSelectedCategory("");
  }, [selectedMonth, currentYear, categoryGroups]);

  // Filter transactions for the selected month and category
  const selectedMonthTransactions = transactions
    .filter((transaction) => {
      const transactionDate = new Date(transaction.date);
      const monthMatch = transactionDate.getMonth() === selectedMonth &&
        transactionDate.getFullYear() === currentYear;
      const categoryMatch = selectedCategory === "" || transaction.category === selectedCategory;
      return monthMatch && categoryMatch;
    })
    .sort((a, b) => {
      const dateComparison =
        new Date(b.date).getTime() - new Date(a.date).getTime();
      if (dateComparison === 0) {
        // If dates are the same, sort by updated_at (most recent first)
        const aUpdatedTime = a.updated_at
          ? new Date(a.updated_at).getTime()
          : 0;
        const bUpdatedTime = b.updated_at
          ? new Date(b.updated_at).getTime()
          : 0;
        return bUpdatedTime - aUpdatedTime;
      }
      return dateComparison;
    });

  // Get selected month's transactions for export
  const getSelectedMonthTransactions = () => {
    return selectedMonthTransactions;
  };

  const exportToJSON = () => {
    const selectedMonthData = getSelectedMonthTransactions();
    const dataStr = JSON.stringify(selectedMonthData, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });

    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `transactions_${currentYear}_${String(
      selectedMonth + 1
    ).padStart(2, "0")}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToExcel = () => {
    const selectedMonthData = getSelectedMonthTransactions();

    // Create CSV content (Excel can open CSV files)
    const headers = ["日期", "描述", "类别", "付款人", "金额"];
    const csvContent = [
      headers.join(","),
      ...selectedMonthData.map((transaction) =>
        [
          new Date(transaction.date).toLocaleDateString(),
          `"${transaction.description}"`,
          `"${transaction.category}"`,
          `"${transaction.payer}"`,
          transaction.amount,
        ].join(",")
      ),
    ].join("\n");

    const dataBlob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `transactions_${currentYear}_${String(
      selectedMonth + 1
    ).padStart(2, "0")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    if (exportFormat === "json") {
      exportToJSON();
    } else {
      exportToExcel();
    }
    setShowExportDropdown(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6">
      <div className="flex justify-end items-center mb-4">
        {/* Export Button with Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowExportDropdown(!showExportDropdown)}
            className="flex items-center space-x-2 bg-slate-700 text-white p-1 rounded-md hover:bg-slate-800 transition-colors duration-300"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </button>

          {showExportDropdown && (
            <div className="absolute right-0 mt-2 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <div className="p-2">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    选择格式:
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="exportFormat"
                        value="json"
                        checked={exportFormat === "json"}
                        onChange={(e) =>
                          setExportFormat(e.target.value as "json" | "excel")
                        }
                        className="mr-2"
                      />
                      <span className="text-sm">JSON</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="exportFormat"
                        value="excel"
                        checked={exportFormat === "excel"}
                        onChange={(e) =>
                          setExportFormat(e.target.value as "json" | "excel")
                        }
                        className="mr-2"
                      />
                      <span className="text-sm">Excel (CSV)</span>
                    </label>
                  </div>
                </div>
                <button
                  onClick={handleExport}
                  className="w-full bg-slate-700 text-white px-3 py-2 rounded-md hover:bg-slate-800 transition-colors text-sm"
                >
                  导出
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-700 mx-auto mb-2"></div>
          <p className="text-gray-600">加载交易记录中...</p>
        </div>
      )}

      {!loading && (
        <div className="overflow-x-auto overflow-y-auto min-h-[350px] max-h-[438px]">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  日期
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  描述
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <span>类别</span>
                    <div className="relative" ref={categoryFilterRef}>
                      <button
                        onClick={() => setShowCategoryFilter(!showCategoryFilter)}
                        className="p-1 rounded hover:bg-gray-200 transition-colors"
                        title="筛选类别"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z"
                          />
                        </svg>
                      </button>

                      {/* Category Filter Dropdown */}
                      {showCategoryFilter && (
                        <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                          <div className="p-2">
                            <div className="mb-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                筛选类别:
                              </label>
                              <button
                                onClick={() => {
                                  setSelectedCategory("");
                                  setShowCategoryFilter(false);
                                }}
                                className={`w-full text-left px-2 py-1 text-sm rounded hover:bg-gray-100 transition-colors ${
                                  selectedCategory === "" ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-700"
                                }`}
                              >
                                所有类别
                              </button>
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              {categoryGroups.map((group) => (
                                <div key={group.category_group}>
                                  <div className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100">
                                    {group.category_group}
                                  </div>
                                  {group.categories.map((budget) => (
                                    <button
                                      key={budget.id}
                                      onClick={() => {
                                        setSelectedCategory(budget.category);
                                        setShowCategoryFilter(false);
                                      }}
                                      className={`w-full text-left px-3 py-1 text-sm rounded hover:bg-gray-100 transition-colors ${
                                        selectedCategory === budget.category ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-700"
                                      }`}
                                    >
                                      {budget.category}
                                    </button>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  付款人
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  金额
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {selectedMonthTransactions.map((transaction) => (
                <tr key={transaction.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(transaction.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 max-w-48 truncate">
                    {transaction.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {transaction.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {transaction.payer}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    {new Intl.NumberFormat("zh-CN", {
                      style: "currency",
                      currency: "CNY",
                    }).format(transaction.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => onEditTransaction(transaction)}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors"
                        title="编辑"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDeleteTransaction(transaction)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="删除"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ExpenseEntry: React.FC<ExpenseEntryProps> = ({ onAddTransaction }) => {
  const { user } = useAuth();
  const { budgetSummary, loading: budgetsLoading } = useGroupBudgets();
  const {
    transactions,
    loading: transactionsLoading,
    addTransaction,
    updateTransaction,
    deleteTransaction,
  } = useMonth();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    description: "",
    category: "",
    amount: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingTransaction, setEditingTransaction] =
    useState<Transaction | null>(null);
  const [editFormData, setEditFormData] = useState({
    date: "",
    description: "",
    category: "",
    amount: "",
  });
  const [deletingTransaction, setDeletingTransaction] =
    useState<Transaction | null>(null);

  const budgetCategoryGroups = useMemo<ApiCategoryGroupBudget[]>(
    () => budgetSummary?.category_groups ?? [],
    [budgetSummary]
  );

  const budgetCategories = useMemo(
    () =>
      budgetCategoryGroups.flatMap((group) =>
        group.categories.map((budget) => budget.category)
      ),
    [budgetCategoryGroups]
  );

  const firstBudgetCategory = budgetCategories[0] ?? "";

  useEffect(() => {
    setFormData((current) => {
      if (current.category && budgetCategories.includes(current.category)) {
        return current;
      }

      const storedCategory = getStoredLastExpenseCategory(user?.group_id);
      const nextCategory = budgetCategories.includes(storedCategory)
        ? storedCategory
        : firstBudgetCategory;

      return {
        ...current,
        category: nextCategory,
      };
    });
  }, [budgetCategories, firstBudgetCategory, user?.group_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) return;

    setIsSubmitting(true);
    try {
      const newTransactionData = {
        date: formData.date,
        description: formData.description,
        category: formData.category,
        payer: user.username,
        amount: parseFloat(formData.amount),
      };

      await onAddTransaction(newTransactionData);
      storeLastExpenseCategory(newTransactionData.category, user.group_id);

      // Add to local state for immediate UI update
      const newTransaction: Transaction = {
        ...newTransactionData,
        id: Date.now().toString(), // Temporary ID
      };
      addTransaction(newTransaction);

      // Reset form but keep date
      setFormData((prev) => ({
        ...prev,
        description: "",
        category: newTransactionData.category,
        amount: "",
      }));
    } catch (error) {
      console.error("Failed to add transaction:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEditTransaction = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    // Ensure date is in YYYY-MM-DD format for date input
    const formattedDate = new Date(transaction.date)
      .toISOString()
      .split("T")[0];
    setEditFormData({
      date: formattedDate,
      description: transaction.description,
      category: transaction.category,
      amount: transaction.amount.toString(),
    });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTransaction || !user) return;

    setIsSubmitting(true);
    try {
      const updatedData = {
        date: editFormData.date,
        description: editFormData.description,
        category: editFormData.category,
        amount: parseFloat(editFormData.amount),
        payer_id: user.user_id!,
      };

      await transactionApi.updateTransaction(
        editingTransaction.id,
        updatedData
      );

      // Update local state
      const updatedTransaction: Transaction = {
        ...editingTransaction,
        ...updatedData,
        payer: user.username,
      };
      updateTransaction(updatedTransaction);

      setEditingTransaction(null);
    } catch (error) {
      console.error("Failed to update transaction:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTransaction = (transaction: Transaction) => {
    setDeletingTransaction(transaction);
  };

  const confirmDeleteTransaction = async () => {
    if (!deletingTransaction) return;

    try {
      await transactionApi.deleteTransaction(deletingTransaction.id);
      deleteTransaction(deletingTransaction.id);
      setDeletingTransaction(null);
    } catch (error) {
      console.error("Failed to delete transaction:", error);
    }
  };

  const cancelDeleteTransaction = () => {
    setDeletingTransaction(null);
  };

  const closeEditModal = () => {
    setEditingTransaction(null);
    setEditFormData({
      date: "",
      description: "",
      category: "",
      amount: "",
    });
  };

  return (
    <section className="mb-12">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 bg-white border border-gray-200 rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold mb-4">添加新支出</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="date"
                className="block text-sm font-medium text-gray-700"
              >
                日期
              </label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                required
              />
            </div>
            <div>
              <label
                htmlFor="amount"
                className="block text-sm font-medium text-gray-700"
              >
                金额 (¥)
              </label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                onWheel={(e) => e.currentTarget.blur()}
                min="0.01"
                step="0.01"
                placeholder="25.50"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                required
              />
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700"
              >
                描述
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="例如：和朋友喝咖啡"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                required
              />
            </div>

            <div>
              <label
                htmlFor="category"
                className="block text-sm font-medium text-gray-700"
              >
                类别
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                required
              >
                <option value="">
                  {budgetsLoading ? "加载预算类别中..." : "请选择类别"}
                </option>
                {budgetCategoryGroups.map((group) => (
                  <optgroup
                    key={group.category_group}
                    label={group.category_group}
                  >
                    {group.categories.map((budget) => (
                      <option key={budget.id} value={budget.category}>
                        {budget.category}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-slate-700 text-white font-bold py-2 px-4 rounded-lg hover:bg-slate-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "添加中..." : "添加支出"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-2">
          <RecentTransactions
            transactions={transactions}
            loading={transactionsLoading || budgetsLoading}
            categoryGroups={budgetCategoryGroups}
            onEditTransaction={handleEditTransaction}
            onDeleteTransaction={handleDeleteTransaction}
          />
        </div>
      </div>

      {/* Edit Modal */}
      {editingTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">编辑交易</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="edit-date"
                  className="block text-sm font-medium text-gray-700"
                >
                  日期
                </label>
                <input
                  type="date"
                  id="edit-date"
                  name="date"
                  value={editFormData.date}
                  onChange={handleEditChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="edit-amount"
                  className="block text-sm font-medium text-gray-700"
                >
                  金额 (¥)
                </label>
                <input
                  type="number"
                  id="edit-amount"
                  name="amount"
                  value={editFormData.amount}
                  onChange={handleEditChange}
                  onWheel={(e) => e.currentTarget.blur()}
                  min="0.01"
                  step="0.01"
                  placeholder="25.50"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="edit-description"
                  className="block text-sm font-medium text-gray-700"
                >
                  描述
                </label>
                <input
                  type="text"
                  id="edit-description"
                  name="description"
                  value={editFormData.description}
                  onChange={handleEditChange}
                  placeholder="例如：和朋友喝咖啡"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="edit-category"
                  className="block text-sm font-medium text-gray-700"
                >
                  类别
                </label>
                <select
                  id="edit-category"
                  name="category"
                  value={editFormData.category}
                  onChange={handleEditChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                >
                  <option value="">请选择类别</option>
                  {budgetCategoryGroups.map((group) => (
                    <optgroup
                      key={group.category_group}
                      label={group.category_group}
                    >
                      {group.categories.map((budget) => (
                        <option key={budget.id} value={budget.category}>
                          {budget.category}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="flex-1 bg-gray-300 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors duration-300"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-slate-700 text-white font-bold py-2 px-4 rounded-lg hover:bg-slate-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "保存中..." : "保存更改"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-red-600">确认删除</h3>
              <button
                onClick={cancelDeleteTransaction}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-4">确定要删除这笔交易吗？</p>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600">
                  <p>
                    <span className="font-medium">日期:</span>{" "}
                    {new Date(deletingTransaction.date).toLocaleDateString()}
                  </p>
                  <p>
                    <span className="font-medium">描述:</span>{" "}
                    {deletingTransaction.description}
                  </p>
                  <p>
                    <span className="font-medium">金额:</span>{" "}
                    {new Intl.NumberFormat("zh-CN", {
                      style: "currency",
                      currency: "CNY",
                    }).format(deletingTransaction.amount)}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={cancelDeleteTransaction}
                className="flex-1 bg-gray-300 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors duration-300"
              >
                取消
              </button>
              <button
                type="button"
                onClick={confirmDeleteTransaction}
                className="flex-1 bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors duration-300"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ExpenseEntry;
